/* @flow */

import { callApiEndpoint } from './index';
import utils from './utils';

import type {
  FindWorklistParams,
  CreateWorklistParams,
  UpdateWorklistParams,
  SimplifiedWorklist,
  SimplifiedTrack,
  Worklist,
  WorklistItem,
  UpdateWorklistItemParams,
  CreateWorklistItemQuotationRequestParams,
} from 'src/types';

const find = async (params: ?FindWorklistParams): Promise<Worklist[]> => {
  const [wlResponse, activeWlResponse] = await Promise.all([
    callApiEndpoint('worklists/findWorklistsByUser'),
    callApiEndpoint('usermanagement/getActiveWorklists'),
  ]);
  return wlResponse.map(utils.toWorklist).map(wl => ({
    ...wl,
    isWorklist1: wl.id === activeWlResponse.activeW1,
    isWorklist2: wl.id === activeWlResponse.activeW2,
  }));
};

const get = async (id: number): Promise<Worklist> => {
  const [wlResponse, activeWlResponse] = await Promise.all([
    callApiEndpoint('worklists/' + id + '/get', {}),
    callApiEndpoint('usermanagement/getActiveWorklists'),
  ]);
  const worklist = {
    ...wlResponse,
    isWorklist1: wlResponse.worklistId === activeWlResponse.activeW1,
    isWorklist2: wlResponse.worklistId === activeWlResponse.activeW2,
  };
  const parsedWorklist = utils.toWorklist(worklist);
  return parsedWorklist;
};

const create = async (params: CreateWorklistParams): Promise<Worklist> => {
  const usageBox = params.usage
    ? {
        dataInizioUtilizzo: params.usage.startDate,
        dataInizioUtilizzoLocked: params.usage.startDateLocked,
        dataFineUtilizzo: params.usage.endDate,
        dataFineUtilizzoLocked: params.usage.endDateLocked,
        esclusiva: params.usage.exclusive,
        esclusivaLocked: params.usage.exclusiveLocked,
        dataFineEsclusiva: params.usage.exclusiveEndDate,
        dataFineEsclusivaLocked: params.usage.exclusiveEndDateLocked,
        estremiEsclusiva: params.usage.exclusiveNotes,
        estremiEsclusivaLocked: params.usage.exclusiveNotesLocked,
        note: params.usage.notes,
        noteLocked: params.usage.notesLocked,
        produzioneId: params.usage.productionId || 0,
        produzioneIdLocked: params.usage.productionLocked,
        puntata: params.usage.episode,
        puntataLocked: params.usage.episodeLocked,
        canaleId: params.usage.channelId || 0,
        canaleIdLocked: params.usage.channelLocked,
        emittenteId: params.usage.broadcastingId || 0,
        emittenteIdLocked: params.usage.broadcastingLocked,
        piattaformaId: params.usage.platformId || 0,
        piattaformaIdLocked: params.usage.platformLocked,
        tipologiaEmissioneId: params.usage.broadcastTypeId || 0,
        tipologiaEmissioneIdLocked: params.usage.broadcastTypeLocked,
        territorioId: params.usage.territoryId || 0,
        territorioIdLocked: params.usage.territoryLocked,
        tipologiaUtilizzoId: params.usage.usageTypeId || 0,
        tipologiaUtilizzoIdLocked: params.usage.usageTypeLocked,
        tipoId: 220002,
      }
    : undefined;

  const response = await callApiEndpoint('worklists/add', {
    method: 'POST',
    body: {
      nome: params.name,
      descrizione: params.description,
      worklistPadreId: params.parentWorklistId || 0,
      usageBox,
    },
  });
  return utils.toWorklist(response);
};

const update = async (params: UpdateWorklistParams): Promise<Worklist> => {
  const updateParams = {
    worklist: {
      worklistId: params.id,
      nome: params.name,
      descrizione: params.description,
      worklistPadreId: params.parentWorklistId || 0,
    },
    usageBox: params.usage
      ? {
          usageBoxId: params.usage.id,
          dataInizioUtilizzo: params.usage.startDate,
          dataInizioUtilizzoLocked: params.usage.startDateLocked,
          dataFineUtilizzo: params.usage.endDate,
          dataFineUtilizzoLocked: params.usage.endDateLocked,
          esclusiva: params.usage.exclusive,
          esclusivaLocked: params.usage.exclusiveLocked,
          dataFineEsclusiva: params.usage.exclusiveEndDate,
          dataFineEsclusivaLocked: params.usage.exclusiveEndDateLocked,
          estremiEsclusiva: params.usage.exclusiveNotes,
          estremiEsclusivaLocked: params.usage.exclusiveNotesLocked,
          note: params.usage.notes,
          noteLocked: params.usage.notesLocked,
          produzioneId: params.usage.productionId || 0,
          produzioneIdLocked: params.usage.productionLocked,
          puntata: params.usage.episode,
          puntataLocked: params.usage.episodeLocked,
          canaleId: params.usage.channelId || 0,
          canaleIdLocked: params.usage.channelLocked,
          emittenteId: params.usage.broadcastingId || 0,
          emittenteIdLocked: params.usage.broadcastingLocked,
          piattaformaId: params.usage.platformId || 0,
          piattaformaIdLocked: params.usage.platformLocked,
          tipologiaEmissioneId: params.usage.broadcastTypeId || 0,
          tipologiaEmissioneIdLocked: params.usage.broadcastTypeLocked,
          territorioId: params.usage.territoryId || 0,
          territorioIdLocked: params.usage.territoryLocked,
          tipologiaUtilizzoId: params.usage.usageTypeId || 0,
          tipologiaUtilizzoIdLocked: params.usage.usageTypeLocked,
        }
      : undefined,
  };
  const [wlResponse, activeWlResponse] = await Promise.all([
    await callApiEndpoint('worklists/update', { method: 'PUT', body: updateParams }),
    callApiEndpoint('usermanagement/getActiveWorklists', {}),
  ]);
  const worklist = {
    ...wlResponse,
    isWorklist1: wlResponse.id === activeWlResponse.activeW1,
    isWorklist2: wlResponse.id === activeWlResponse.activeW2,
  };
  return utils.toWorklist(worklist);
};

const _delete = async (id: number): Promise<any> => {
  await callApiEndpoint('worklists/' + id + '/delete', { method: 'DELETE' });
  return id;
};

const findItems = async (id: number): Promise<WorklistItem[]> => {
  const items = await callApiEndpoint('worklistitems/' + id + '/findWorklistItemsByWorklistId', {
    worklistId: id,
  });
  return items.map(utils.toWorklistItem);
};

const createItems = async (
  tracks: SimplifiedTrack[],
  worklist: SimplifiedWorklist,
  usageId: ?number
): Promise<WorklistItem[]> => {
  const response =
    usageId !== null
      ? await callApiEndpoint('worklistitems/addWithUsage', {
          method: 'POST',
          body: {
            tracceId: tracks.map(track => track.id),
            targetWorklistId: worklist.id,
            usageBoxId: usageId || 0,
          },
        })
      : await callApiEndpoint('worklistitems/add', {
          method: 'POST',
          body: {
            tracceId: tracks.map(track => track.id),
            targetWorklistId: worklist.id,
          },
        });
  return response.map(utils.toWorklistItem);
};

const updateItem = async (params: UpdateWorklistItemParams): Promise<WorklistItem> => {
  const updateParams = {
    _areaId: params.areaId || 0, // "areaId": -1,
    _canaleId: params.channelId || 0, // "canaleId": 38,
    _dataFineEsclusiva: params.exclusiveEndDate,
    _dataFineUtilizzo: params.endDate,
    _dataInizioUtilizzo: params.startDate,
    _emittenteId: params.broadcastingId || 0, // "emittenteId": 87362,
    _esclusiva: params.exclusive,
    _estremiEsclusiva: params.exclusiveNotes,
    _note: params.notes,
    _piattaformaId: params.platformId,
    _produzioneId: params.productionId || 0,
    _puntata: params.episode,
    _territorioId: params.territoryId || 0,
    _tipologiaEmissioneId: params.broadcastTypeId || 0, //
    _tipologiaUtilizzoId: params.usageTypeId || 0,
    _tracciaId: params.trackId,
    _worklistId: params.worklistId,
    _worklistItemId: params.id,
  };
  const response = await callApiEndpoint('worklistitems/update', {
    method: 'PUT',
    body: updateParams,
  });
  return utils.toWorklistItem(response);
};

const deleteItems = async (worklistItems: WorklistItem[]): Promise<number[]> => {
  const response = await callApiEndpoint('worklistitems/delete', {
    method: 'DELETE',
    body: worklistItems.map(item => item.id),
  });
  return response.map(item => item.id);
};

const updateWl1 = async (id: number): Promise<Worklist[]> => {
  let pathUrl = id || 2;

  await callApiEndpoint(
    'usermanagement/' + pathUrl + '/updateActiveW1',
    //  worklistId: id,
    { method: 'PUT' }
  );
  const worklists = await find();
  return worklists;
};

const updateWl2 = async (id: number): Promise<Worklist[]> => {
  let pathUrl = id || 2;

  await callApiEndpoint(
    'usermanagement/' + pathUrl + '/updateActiveW2',
    //  worklistId: id,
    { method: 'PUT' }
  );
  const worklists = await find();
  return worklists;
};

const createWorklistItemQuotationRequest = async (
  params: CreateWorklistItemQuotationRequestParams
): Promise<WorklistItem> => {
  const response = await callApiEndpoint('worklistitems/requestWorklistItemComponentQuotations', {
    method: 'POST',
    body: {
      worklistItemId: params.worklistItemId,
      //tipoDirittoIds: params.rightIds,
      tipoDirittoIds: [6, 8],
    },
  });
  return utils.toWorklistItem(response);
};

export default {
  find,
  get,
  create,
  update,
  delete: _delete,
  wl1: {
    update: updateWl1,
  },
  wl2: { update: updateWl2 },
  items: {
    find: findItems,
    create: createItems,
    update: updateItem,
    delete: deleteItems,
  },
  askQuotation: createWorklistItemQuotationRequest,
};
